import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';

// --- Pulled from Jotai internals
// Convert `atomWithHash` -> query params

type Unsubscribe = () => void;

interface SyncStorage<Value> {
  getItem: (key: string) => Value;
  setItem: (key: string, newValue: Value) => void;
  removeItem: (key: string) => void;
  delayInit?: boolean;
  subscribe?: (key: string, callback: (value: Value) => void) => Unsubscribe;
}

function atomWithQuery(
  key: string,
  initialValue?: string,
  options?: {
    serialize?: (val: string) => string;
    deserialize?: (str: string) => string;
    delayInit?: boolean;
    replaceState?: boolean;
    subscribe?: (callback: () => void) => () => void;
  },
) {
  const serialize = options?.serialize || JSON.stringify;
  const deserialize = options?.deserialize || JSON.parse;
  const subscribe =
    options?.subscribe ||
    ((callback) => {
      window.addEventListener('searchchange', callback);

      return () => {
        window.removeEventListener('searchchange', callback);
      };
    });
  const qParamStorage: SyncStorage<string | undefined> = {
    getItem: (k) => {
      const searchParams = new URLSearchParams(window.location.search.slice(1));
      const storedValue = searchParams.get(k);
      if (storedValue === null) {
        return '';
      }

      return deserialize(storedValue);
    },
    setItem: (k, newValue) => {
      const searchParams = new URLSearchParams(window.location.search.slice(1));
      if (!newValue) {
        searchParams.delete(k);
      } else {
        searchParams.set(k, serialize(newValue));
      }
      if (options?.replaceState) {
        const queryString =
          searchParams.toString().length > 0 ? `?${searchParams.toString()}` : '';
        window.history.replaceState(null, '', queryString);
      } else {
        window.location.search = searchParams.toString();
      }
    },
    removeItem: (k) => {
      const searchParams = new URLSearchParams(window.location.search.slice(1));
      searchParams.delete(k);
      if (options?.replaceState) {
        window.history.replaceState(null, '', `?${searchParams.toString()}`);
      } else {
        window.location.search = searchParams.toString();
      }
    },
    ...(options?.delayInit && { delayInit: true }),
    subscribe: (k, setValue) => {
      const callback = () => {
        const searchParams = new URLSearchParams(window.location.search.slice(1));
        const str = searchParams.get(k);
        if (str !== null) {
          setValue(deserialize(str));
        } else {
          setValue(initialValue);
        }
      };

      return subscribe(callback);
    },
  };

  return atomWithStorage(key, initialValue, qParamStorage);
}

/** ********************************* */

const atomWithQueryParam = (key: string) =>
  atomWithQuery(key, '', {
    replaceState: true,
    serialize: encodeURI,
    deserialize: decodeURI,
  });

const addressAtom = atomWithQueryParam(QUERY_PARAM.address);
const searchAtom = atomWithQueryParam(QUERY_PARAM.search);
const selectedGroupAtom = atomWithQueryParam(QUERY_PARAM.dashboardSelectedGroup);
const selectedCollectionAtom = atomWithQueryParam('collection');
const sharedCollectionAtom = atomWithQueryParam('shared');
const currentLeadAtom = atomWithQueryParam('lead');
const zoomAtom = atomWithQueryParam(QUERY_PARAM.zoom);
const pageQuery = atomWithQueryParam(QUERY_PARAM.page);
const coordinatesQueryAtom = atomWithQueryParam(QUERY_PARAM.coordinates);
const cloneQueryAtom = atomWithQueryParam(QUERY_PARAM.clone);
const centerQueryAtom = atomWithQueryParam(QUERY_PARAM.center);
const mapFiltersQueryAtom = atomWithQueryParam(QUERY_PARAM.mapFilters);
const regionQueryAtom = atomWithQueryParam(QUERY_PARAM.regionCode);
const stateCodeQueryAtom = atomWithQueryParam(QUERY_PARAM.stateCode);
const isDocumentViewAtom = atomWithQueryParam(QUERY_PARAM.isDocumentView);

export const useAddressQuery = () => useAtom(addressAtom);
export const useSearchQuery = () => useAtom(searchAtom);
export const useSelectedGroupQuery = () => useAtom(selectedGroupAtom);
export const useSelectedCollectionQuery = () => useAtom(selectedCollectionAtom);
export const useSelectedSharedCollectionQuery = () => useAtom(sharedCollectionAtom);
export const useCurrentLeadQuery = () => useAtom(currentLeadAtom);
export const useZoomQuery = () => useAtom(zoomAtom);
export const isDocumentViewQuery = () => useAtom(isDocumentViewAtom);

export const useCloneQuery = () => {
  const [cloneQuery, setCloneQuery] = useAtom(cloneQueryAtom);

  return { cloneQuery, setCloneQuery };
};

const useQuery = () => {
  return useMemo(
    () => new URLSearchParams(window.location.search.slice(1)),
    [window.location.search],
  );
};

export const useGroupIdQuery = () => {
  const [, setGroupIdQuery] = useSelectedGroupQuery();
  const searchParams = useQuery();
  const groupIdQuery = searchParams.get(QUERY_PARAM.dashboardSelectedGroup);

  return {
    groupIdQuery,
    setGroupIdQuery,
  };
};

export const useMapFiltersQuery = () => {
  const [, setMapFiltersQuery] = useAtom(mapFiltersQueryAtom);
  const searchParams = useQuery();
  const mapFiltersQuery = searchParams.get(QUERY_PARAM.mapFilters);

  return {
    mapFiltersQuery,
    setMapFiltersQuery,
  };
};

export const useRegionQuery = () => {
  const [, setRegionQuery] = useAtom(regionQueryAtom);
  const searchParams = useQuery();
  const regionQuery = searchParams.get(QUERY_PARAM.regionCode);

  return {
    regionQuery,
    setRegionQuery,
  };
};

export const useStateCodeQuery = () => {
  const [, setStateCodeQuery] = useAtom(stateCodeQueryAtom);
  const searchParams = useQuery();
  const stateCodeQuery = searchParams.get(QUERY_PARAM.stateCode);

  return {
    stateCodeQuery,
    setStateCodeQuery,
  };
};

export const useCenterQuery = () => {
  const [centerQuery, setCenterQuery] = useAtom(centerQueryAtom);

  return {
    centerQuery,
    setCenterQuery,
  };
};

export const useCoordinatesQuery = () => {
  const [coordinatesQuery, setCoordinatesQuery] = useAtom(coordinatesQueryAtom);

  const coordinatesAsLatLng = useMemo(() => {
    if (!coordinatesQuery || typeof coordinatesQuery !== 'string') {
      return { lat: 0, lng: 0 };
    }
    const [lat, lng] = coordinatesQuery.split(',');

    return { lat: parseFloat(lat), lng: parseFloat(lng) };
  }, [coordinatesQuery]);

  return { coordinatesQuery, setCoordinatesQuery, coordinatesAsLatLng };
};
export const usePageQuery = () => {
  const pageQueryDefaultValue = 1;
  const [pageQueryString, setPageQueryString] = useAtom(pageQuery);

  if (pageQueryString === undefined || Number(pageQueryString) < 1) {
    setPageQueryString(pageQueryDefaultValue.toString());
  }

  return [pageQueryString, setPageQueryString] as const;
};

export const useEmailQuery = () => {
  const searchParams = useQuery();
  const emailQuery = searchParams.get(QUERY_PARAM.email);

  return emailQuery;
};

export const useInviteCodeQuery = () => {
  const searchParams = useQuery();

  return searchParams.get(QUERY_PARAM.invite_code);
};
